<template>
    <validation-observer v-slot="{ invalid, handleSubmit }" ref="simulationValidator">
        <form @submit.prevent="handleSubmit(submit)" @keydown="errors.clear($event.target.name)">

            <base-input class="mb-3"
                :label="$t('common.name')"
                label-classes="form-control-label required"
                :placeholder="$t('common.name')"
                v-model="value.name"
                name="name"
                rules="required"
                :disabled="published && !duplicating"
                :error="errors.get('name')">
            </base-input>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('common.description')"
                name="description"
                :error="errors.get('description')"
            >
                <textarea class="form-control" rows="5"
                    :placeholder="$t('common.description')"
                    v-model="value.description"
                ></textarea>
            </base-input>

            <base-select class="mb-4" v-if="!duplicating && !value.assessment"
                :label="$t('simulations.grid-for') + positionName(1)"
                :placeholder="$t('simulations.grid-for') + positionName(1)"
                v-model="value.grid1_id"
                :options="positionGridsLabels(1)"
                :notes="positionGridsNotes(1)"
                @input="errors.clear('grid1_id')"
                :error="errors.get('grid1_id')"
                :disabled="published"
                clearable
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating && degree.simulation_role_2 && !value.assessment"
                :label="$t('simulations.grid-for') + positionName(2)"
                :placeholder="$t('simulations.grid-for') + positionName(2)"
                v-model="value.grid2_id"
                :options="positionGridsLabels(2)"
                :notes="positionGridsNotes(2)"
                @input="errors.clear('grid2_id')"
                :error="errors.get('grid2_id')"
                :disabled="published"
                clearable
            >
            </base-select>

            <base-checkbox class="mb-3" v-if="!duplicating && !value.assessment"
                v-model="value.remedial"
                :disabled="published"
            >
                {{ $t('simulations.remedial') }}
            </base-checkbox>

            <base-checkbox class="mb-3" v-if="value.assessment"
                v-model="value.private"
            >
                {{ $t('assessments.private-assessment') }}
            </base-checkbox>

            <date-input class="mb-3" v-if="!value.remedial && !value.assessment"
                :label="$t('common.edition')"
                label-classes="form-control-label required"
                name="edition"
                rules="required"
                v-model="value.edition"
                :error="errors.get('edition')"
                ref="date">
            </date-input>

            <div class="category form-category mb-4">
                <small><span class="text-warning">*</span> {{ $t('common.required-fields') }}</small>
            </div>

            <div class="text-center">
                <base-button native-type="submit"
                    type="primary"
                    :disabled="errors.any() || invalid"
                >
                    {{ value.id ? (duplicating ? $t('common.duplicate') : $t('common.update')) : $t('common.create') }}
                </base-button>

                <base-button type="link" class="ml-auto" @click="modal.show = false">
                    {{ $t('common.cancel') }}
                </base-button>
            </div>

        </form>
    </validation-observer>
</template>

<script>
    export default {

        props: {

            value: {
                type: Object,
                required: true
            },

            errors: {
                type: Object,
                required: true
            },

            modal: {
                type: Object,
                default: null
            },

            duplicating: {
                type: Boolean,
                default: false
            },

            degree: {
                type: Object,
                required: true
            },

            grids: {
                type: Object,
                required: true
            },
        },

        created() {
            this.errors.setValidator('simulationValidator', this)
        },

        watch: {

            duplicating(val) {
                if (this.duplicating) {
                    this.value.name += ' (copy)'
                }
            }
        },

        computed: {

            published() {
                return this.degree.status == 'active'
            }
        },

        methods: {
            
            positionName(position) {
                let nameObject = this.degree['simulation_role_' + position + '_name'];
                if (!nameObject) {
                    // Still loading.
                    return '...'
                }
                return ' ' + nameObject[this.degree.lang]
            },

            positionGridsLabels(position) {
                let labels = {}
                let grids = this.positionGrids(position)
                for (let gridId in grids) {
                    labels[gridId] = grids[gridId].name
                }
                return labels
            },

            positionGridsNotes(position) {
                let notes = {}
                let grids = this.positionGrids(position)
                for (let gridId in grids) {
                    notes[gridId] = grids[gridId].edition
                }
                return notes
            },

            positionGrids(position) {
                const currentGrid = this.value['grid' + position];
                const currentGridId = this.value['grid' + position + '_id'];
                const simulationRole = this.degree['simulation_role_' + position];

                const gridsSelect = {}
                for (const gridId in this.grids) {
                    const gridObject = this.grids[gridId]

                    // Not the right language.
                    if (gridObject.lang != this.degree.lang) {
                        continue
                    }
                    
                    // Not the right simulation role.
                    if (gridObject.simulation_role != simulationRole && gridObject.simulation_role != 'neutral') {
                        continue
                    }
                    
                    // Keep it.
                    gridsSelect[gridId] = gridObject
                }

                // If the current simulation grid is not in the list, we must add it just to have a consistent display.
                // This may happen when a grid is removed after it has been selected in the degree.
                // Saving the form with this grid will return an error, which is right.
                if (currentGridId && !gridsSelect[currentGridId]) {
                    gridsSelect[currentGridId] = currentGrid
                }

                // Add the edition just after the name.
                // grids = Object.keys(grids).map(id => grids[id].name + ' ')
                return gridsSelect
            },

            submit() {
                this.$emit('input', { ...this.value})
            },
        }
    }
</script>
